import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
  Table,
} from 'react-bootstrap';
import { Link } from 'react-router-dom';
import logo from '../../assets/LogoHorizontal.png';

function formatProductWeight(
  value: number | null | undefined,
  unit: 'g' | 'kg' | null | undefined,
) {
  if (value == null || Number.isNaN(value)) return null;

  const formattedValue = Number.isInteger(value)
    ? String(value)
    : value.toLocaleString(undefined, { maximumFractionDigits: 2 });

  return `${formattedValue} ${unit === 'kg' ? 'kg' : 'g'}`;
}

export type PublicProductDppPayload = {
  productId: string;
  passportId: string;
  product: {
    name: string;
    skuModel: string;
    gtinEan: string | null;
    brandName: string;
    categoryCode: string;
    categoryName: string;
    description: string | null;
    imageUrl: string | null;
  };
  metadata: {
    productWeightValue: number | null;
    productWeightUnit: 'g' | 'kg' | null;
    manufacturerName: string | null;
    manufacturerAddress: string | null;
    importedOutsideEu: boolean;
    importerName: string | null;
    importerEmail: string | null;
    importerAddress: string | null;
    seasonCollection: string | null;
    targetAudience: string[];
    machineClassification: string | null;
    ceResponsiblePerson: string | null;
    ceResponsiblePersonEntityType: string | null;
    ceResponsiblePersonCompanyName: string | null;
    ceResponsiblePersonEuAddress: string | null;
    ceResponsiblePersonContactEmail: string | null;
    includeConsumerPackaging: boolean;
    includeSupplyChainTraceability: boolean;
    lifespanUnit: 'years' | 'months';
    softwareSupportExpiry: string | null;
    energyEfficiencyRating: string | null;
  };
  materials: Array<{
    materialName: string;
    weightPercentage: number;
    recycledContentPercentage: number | null;
    isRecyclable: boolean;
  }>;
  packaging: Array<{
    packagingCategory: string;
    materialType: string;
    weightGrams: number;
    pcrContentPercent: number;
    recyclabilityStatus: string | null;
  }>;
  reach: {
    svhcStatus: 'none' | 'contains' | '';
    svhcDeclarations: Array<{
      substanceName: string | null;
      casEcNumber: string | null;
    }>;
  };
  rohs: {
    rohsStatus: 'without_exemption' | 'with_exemption' | 'not_applicable' | '';
    exemptionCode?: string | null;
    customExemptionDetails?: string | null;
    exclusionReason?: string | null;
    supportingDocument?: {
      title?: string | null;
      url?: string | null;
    } | null;
  };
  popsPfas: {
    status: 'not_verified' | 'verified_compliant' | 'contains_restricted' | '';
    details?: string | null;
    detectedMaterials?: string[];
    assessmentNote?: string | null;
    supportingDocument?: {
      title?: string | null;
      url?: string | null;
    } | null;
  };
  machineryDeclaration?: {
    kind?: 'doc' | 'doi' | null;
    title?: string | null;
    url?: string | null;
  } | null;
  battery?: {
    ratedCapacityAh?: number | null;
    ratedEnergyKwh?: number | null;
    nominalVoltageV?: number | null;
    minimumVoltageV?: number | null;
    maximumVoltageV?: number | null;
    powerCapabilityLimits?: string | null;
    roundTripEfficiencyPercent?: number | null;
    cycleLifetimeDescriptor?: string | null;
    operatingTemperatureWindow?: string | null;
    extinguishingAgents?: string | null;
    safetyHandlingMeasures?: string | null;
  } | null;
  circularity: {
    carbonFootprint: number | null;
    expectedLifespan: number | null;
    repairabilityScore: number | null;
    repairManual: string | null;
    disassemblyGuide?: string | null;
    microplasticsReport?: {
      title?: string | null;
      url?: string | null;
    } | null;
    recyclingInstructions: string | null;
    waterConsumption: number | null;
    careSymbols: string[];
    colorFastnessRating: number | null;
    pillingResistanceRating: number | null;
    sparePartsAvailability: string | null;
    takeBackScheme: string | null;
    microplasticsStatus: string | null;
  };
  categorySpecific?: {
    construction?: {
      vocEmissionClass?: string | null;
      dopDocument?: {
        title?: string | null;
        url?: string | null;
      } | null;
      demolitionGuide?: {
        title?: string | null;
        url?: string | null;
      } | null;
    } | null;
    furniture?: {
      eudrDdsReference?: string | null;
      flameRetardantsPresent?: boolean;
      flameRetardantChemicalNames?: string[];
      hasRemovableCovers?: boolean | null;
    } | null;
    metals?: {
      productionRoute?: string | null;
      cbamEmbeddedEmissions?: number | null;
    } | null;
    tires?: {
      rollingResistanceClass?: string | null;
      wetGripClass?: string | null;
      abrasionRateMgKm?: number | null;
      isRetreadable?: boolean | null;
    } | null;
    chemicals?: {
      ufiCode?: string | null;
      clpHazardPhrases?: string[];
      surfactantBiodegradabilityPercentage?: number | null;
    } | null;
  } | null;
  passport: {
    status: 'PUBLISHED' | 'ARCHIVED' | 'WITHDRAWN';
    statusReason: string | null;
    statusUpdatedAt: string | null;
    uniqueProductIdentifier: string;
    publicUrl: string | null;
    qrCodeAssetUrl: string | null;
    publishedAt: string | null;
    visibilitySettings?: {
      technicalSpecsAccess?: 'public' | 'restricted';
    };
  };
  access?: {
    grantedTier: 'OPEN' | 'RESTRICTED' | 'CONFIDENTIAL';
    hasExtendedAccess: boolean;
    hasDocumentAccess?: boolean;
    tokenHolderEmail: string | null;
  };
  extendedData?: {
    traceability: Array<{
      tier: 'tier_1' | 'tier_2' | 'tier_3';
      companyName: string;
      countryCode: string;
      facilityIdOrAddress: string;
      processComponent: string;
    }>;
    compliance: {
      importerEori: string | null;
      hardwareRevision: string | null;
      criticalRawMaterials: boolean;
      criticalRawMaterialsList: string[];
      weeeRegistrationNumber: string | null;
      rohsExemptionCode: string | null;
      customExemptionDetails: string | null;
      rohsExclusionReason: string | null;
    };
  } | null;
};

type PublicProductDppViewProps = {
  payload: PublicProductDppPayload;
  accessRequestSuccess?: string | null;
  onRequestAccess?: (() => void) | null;
  previewMode?: boolean;
  showHomeLink?: boolean;
};

const formatNumber = (value: number | null | undefined, suffix = '') => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toLocaleString()}${suffix}`;
};

const formatPercent = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '-';
  return `${Number(value).toFixed(2)}%`;
};

const formatDate = (value: string | null | undefined) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString();
};

const titleCase = (value: string | null | undefined) => {
  if (!value) return '-';
  return value
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
};

const getPackagingCategoryLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'primary':
      return 'Primary / Sales Packaging';
    case 'secondary':
      return 'Secondary / Grouped Packaging';
    case 'tertiary':
    case 'ecommerce':
      return 'Tertiary / Transport Packaging';
    default:
      return titleCase(value);
  }
};

const hasText = (value: string | null | undefined) =>
  typeof value === 'string' && value.trim().length > 0;

const getMachineClassificationLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'STANDARD_MACHINERY':
      return 'Standard Machinery - Self-Declaration';
    case 'HIGH_RISK_MACHINERY':
      return 'High-Risk Machinery - Annex I Notified Body Required';
    case 'PARTLY_COMPLETED_MACHINERY':
    case 'PARTLY_COMPLETED':
      return 'Partly Completed Machinery - Declaration of Incorporation';
    default:
      return value;
  }
};

const isPartlyCompletedMachinery = (value: string | null | undefined) =>
  value === 'PARTLY_COMPLETED' || value === 'PARTLY_COMPLETED_MACHINERY';

const getMachineryDeclarationTitle = (
  declaration: PublicProductDppPayload['machineryDeclaration'],
) => {
  if (declaration?.kind === 'doi') {
    return 'EU Declaration of Incorporation (PDF)';
  }

  return 'EU Declaration of Conformity (PDF)';
};

const getCeResponsibleEntityTypeLabel = (value: string | null | undefined) => {
  switch (value) {
    case 'EU_MANUFACTURER':
      return 'EU Manufacturer';
    case 'EU_IMPORTER':
      return 'EU Importer';
    case 'AUTHORIZED_REPRESENTATIVE':
      return 'Authorized Representative';
    default:
      return value;
  }
};

const hasNumber = (value: number | null | undefined) =>
  value !== null && value !== undefined;

const getPublicPassportBadgeVariant = (
  status: PublicProductDppPayload['passport']['status'],
) => {
  switch (status) {
    case 'ARCHIVED':
      return 'warning';
    case 'WITHDRAWN':
      return 'danger';
    default:
      return 'success';
  }
};

const getRohsStatusLabel = (
  status: PublicProductDppPayload['rohs']['rohsStatus'],
) => {
  switch (status) {
    case 'without_exemption':
      return 'Compliant without exemption';
    case 'with_exemption':
      return 'Compliant with exemption';
    case 'not_applicable':
      return 'Not applicable';
    default:
      return 'Not declared';
  }
};

const getPopsPfasStatusLabel = (
  status: PublicProductDppPayload['popsPfas']['status'],
) => {
  switch (status) {
    case 'verified_compliant':
      return 'Verified compliant';
    case 'contains_restricted':
      return 'Contains restricted substances / exemption applies';
    case 'not_verified':
      return 'Not verified yet';
    default:
      return 'Not declared';
  }
};

const getBatteryPfasStatusLabel = (
  popsPfas: PublicProductDppPayload['popsPfas'],
) => {
  const detectedMaterials = popsPfas.detectedMaterials ?? [];

  if (popsPfas.status === 'contains_restricted') {
    const details = popsPfas.details || detectedMaterials.join(', ');
    return details ? `Contains ${details}` : 'Declared';
  }

  if (popsPfas.status === 'verified_compliant') {
    return 'Declared / Not Present above threshold';
  }

  if (detectedMaterials.length > 0) {
    return `Contains ${detectedMaterials.join(', ')}`;
  }

  return 'Not Present above threshold';
};

export function PublicProductDppView({
  payload,
  accessRequestSuccess = null,
  onRequestAccess = null,
  previewMode = false,
  showHomeLink = true,
}: PublicProductDppViewProps) {
  const productWeight = formatProductWeight(
    payload.metadata.productWeightValue,
    payload.metadata.productWeightUnit,
  );
  const targetAudience = payload.metadata.targetAudience.join(', ');
  const ceResponsiblePersonSummary = [
    payload.metadata.ceResponsiblePersonCompanyName,
    payload.metadata.ceResponsiblePersonEuAddress,
  ]
    .filter((value): value is string => hasText(value))
    .join(', ');
  const passportStatus = payload.passport.status;
  const isArchived = passportStatus === 'ARCHIVED';
  const isWithdrawn = passportStatus === 'WITHDRAWN';
  const grantedTier = payload.access?.grantedTier ?? 'OPEN';
  const isConfidentialTier = grantedTier === 'CONFIDENTIAL';
  const hasExtendedAccess = payload.access?.hasExtendedAccess ?? false;
  const hasDocumentAccess = payload.access?.hasDocumentAccess ?? false;
  const technicalSpecsAccess =
    payload.passport.visibilitySettings?.technicalSpecsAccess ?? 'public';
  const technicalSpecsVisible =
    technicalSpecsAccess === 'public' || hasExtendedAccess;
  const isBatteryProduct = payload.product.categoryCode === 'BATTERIES_STORAGE';
  const isConstructionProduct =
    payload.product.categoryCode === 'CONSTRUCTION_PRODUCTS';
  const isFurnitureProduct =
    payload.product.categoryCode === 'FURNITURE_MATTRESSES';
  const isMetalsProduct = payload.product.categoryCode === 'IRON_STEEL_METALS';
  const isTiresProduct = payload.product.categoryCode === 'TIRES_AUTOMOTIVE';
  const isChemicalsProduct = payload.product.categoryCode === 'CHEMICALS';
  const isPartlyCompletedMachine = isPartlyCompletedMachinery(
    payload.metadata.machineClassification,
  );
  const popsPfasAppliesToProduct =
    payload.product.categoryCode === 'TEXTILES_APPAREL' || isBatteryProduct;

  return (
    <Container className="py-4 py-lg-5" style={{ maxWidth: '1120px' }}>
      <style>
        {`
          .public-dpp-hero {
            background: linear-gradient(135deg, #f7f4ec 0%, #ffffff 62%, #edf6f2 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
            border-radius: 1.5rem;
            overflow: hidden;
          }

          .public-dpp-hero-content,
          .public-dpp-hero-copy,
          .public-dpp-hero-meta,
          .public-dpp-hero-side {
            min-width: 0;
          }

          .public-dpp-hero-copy {
            flex: 1 1 auto;
          }

          .public-dpp-hero-meta {
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .public-dpp-hero-identifiers {
            overflow-wrap: anywhere;
            word-break: break-word;
          }

          .public-dpp-hero-identifiers > span {
            min-width: 0;
          }

          .public-dpp-card {
            border-radius: 1.25rem;
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .public-dpp-image {
            width: 112px;
            height: 112px;
            border-radius: 1.5rem;
            object-fit: cover;
            background: linear-gradient(135deg, #f0ede4 0%, #dfece5 100%);
            border: 1px solid rgba(28, 52, 43, 0.08);
          }

          .public-dpp-image-placeholder {
            display: flex;
            align-items: center;
            justify-content: center;
            color: #385246;
            font-size: 2rem;
          }

          @media (max-width: 575.98px) {
            .public-dpp-hero-copy {
              flex-direction: column;
            }

            .public-dpp-image {
              width: 96px;
              height: 96px;
            }
          }
        `}
      </style>

      <div className="d-flex align-items-center justify-content-between gap-3 mb-4">
        {showHomeLink ? (
          <Link
            to="/"
            className="text-decoration-none text-dark d-inline-flex align-items-center gap-2"
          >
            <img src={logo} alt="SupplyPassport" height="30" />
          </Link>
        ) : (
          <img src={logo} alt="SupplyPassport" height="30" />
        )}
        <div className="d-flex align-items-center gap-2">
          <Badge
            bg={previewMode ? 'info' : hasExtendedAccess ? 'info' : 'success'}
          >
            {previewMode
              ? 'Public Preview'
              : hasExtendedAccess
                ? `${grantedTier} Access`
                : 'Public DPP'}
          </Badge>
        </div>
      </div>

      {accessRequestSuccess && (
        <Alert variant="success" className="mb-4">
          {accessRequestSuccess}
        </Alert>
      )}

      {isArchived && (
        <Alert variant="warning" className="mb-4">
          This passport has been archived. The information below reflects the
          last published version and may no longer represent an active product.
          {payload.passport.statusReason
            ? ` Reason: ${payload.passport.statusReason}`
            : ''}
        </Alert>
      )}

      {isWithdrawn && (
        <Alert variant="danger" className="mb-4">
          This passport has been withdrawn and its technical specifications are
          no longer publicly available.
          {payload.passport.statusReason
            ? ` Reason: ${payload.passport.statusReason}`
            : ''}
        </Alert>
      )}

      <Card className="public-dpp-hero shadow-sm mb-4">
        <Card.Body className="p-4 p-lg-5">
          <div className="public-dpp-hero-content d-flex flex-column flex-lg-row justify-content-between gap-4">
            <div className="public-dpp-hero-copy d-flex gap-3 align-items-start">
              {payload.product.imageUrl ? (
                <img
                  src={payload.product.imageUrl ?? undefined}
                  alt={payload.product.name}
                  className="public-dpp-image"
                />
              ) : (
                <div className="public-dpp-image public-dpp-image-placeholder">
                  <i className="bi bi-box-seam" />
                </div>
              )}
              <div className="public-dpp-hero-meta">
                <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
                  <h1 className="h2 mb-0">{payload.product.name}</h1>
                  <Badge bg="light" text="dark" className="border">
                    {payload.product.categoryName ||
                      payload.product.categoryCode}
                  </Badge>
                  <Badge
                    bg={
                      previewMode
                        ? 'info'
                        : getPublicPassportBadgeVariant(payload.passport.status)
                    }
                  >
                    {previewMode ? 'PREVIEW' : payload.passport.status}
                  </Badge>
                  {payload.machineryDeclaration && (
                    <Badge
                      bg={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'light'
                          : 'success'
                      }
                      text={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'dark'
                          : undefined
                      }
                      className={
                        payload.machineryDeclaration.kind === 'doi'
                          ? 'border'
                          : undefined
                      }
                    >
                      <i className="bi bi-check-lg me-1" />
                      {payload.machineryDeclaration.kind === 'doi'
                        ? 'Declaration of Incorporation Present'
                        : 'CE Declaration Present'}
                    </Badge>
                  )}
                </div>
                <div className="text-muted mb-2">
                  {payload.product.brandName}
                </div>
                <div className="public-dpp-hero-identifiers small text-muted d-flex flex-wrap gap-3">
                  <span>SKU / Model: {payload.product.skuModel}</span>
                  <span>GTIN / EAN: {payload.product.gtinEan || '-'}</span>
                  <span>UPI: {payload.passport.uniqueProductIdentifier}</span>
                  {productWeight && <span>Weight: {productWeight}</span>}
                </div>
              </div>
            </div>
            <div className="public-dpp-hero-side text-lg-end">
              <div className="small text-muted">
                {previewMode
                  ? 'Preview'
                  : isArchived || isWithdrawn
                    ? 'Status Updated'
                    : 'Published'}
              </div>
              <div className="fw-semibold mb-3">
                {previewMode
                  ? 'Unpublished draft'
                  : formatDate(
                      payload.passport.statusUpdatedAt ??
                        payload.passport.publishedAt,
                    )}
              </div>
            </div>
          </div>
          {payload.product.description && (
            <p className="text-muted mt-4 mb-0">
              {payload.product.description}
            </p>
          )}
        </Card.Body>
      </Card>

      {isWithdrawn ? (
        <Card className="public-dpp-card shadow-sm">
          <Card.Body className="p-4 p-lg-5">
            <div className="text-uppercase small fw-semibold text-muted mb-3">
              Withdrawal Notice
            </div>
            <h2 className="h4 mb-3">Public specifications removed</h2>
            <p className="text-muted mb-3">
              This Digital Product Passport has been withdrawn. Detailed
              materials, compliance, and circularity specifications are
              intentionally hidden from the public route.
            </p>
            <dl className="row mb-0">
              <dt className="col-sm-4">Product</dt>
              <dd className="col-sm-8">{payload.product.name}</dd>
              <dt className="col-sm-4">Brand</dt>
              <dd className="col-sm-8">{payload.product.brandName}</dd>
              <dt className="col-sm-4">SKU / Model</dt>
              <dd className="col-sm-8">{payload.product.skuModel}</dd>
              <dt className="col-sm-4">Reason</dt>
              <dd className="col-sm-8">
                {payload.passport.statusReason || '-'}
              </dd>
            </dl>
          </Card.Body>
        </Card>
      ) : (
        <Row className="g-4">
          <Col xs={12}>
            <Card className="public-dpp-card shadow-sm h-100">
              <Card.Body className="p-4">
                <div className="text-uppercase small fw-semibold text-muted mb-3">
                  Economic Operator
                </div>
                <dl className="row mb-0">
                  {hasText(payload.metadata.manufacturerName) && (
                    <>
                      <dt className="col-sm-5">Manufacturer</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.manufacturerName}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.manufacturerAddress) && (
                    <>
                      <dt className="col-sm-5">Manufacturer Address</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.manufacturerAddress}
                      </dd>
                    </>
                  )}
                  <dt className="col-sm-5">Imported Outside EU</dt>
                  <dd className="col-sm-7">
                    {payload.metadata.importedOutsideEu ? 'Yes' : 'No'}
                  </dd>
                  {hasText(payload.metadata.importerName) && (
                    <>
                      <dt className="col-sm-5">EU Importer</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerName}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.importerEmail) && (
                    <>
                      <dt className="col-sm-5">Importer Email</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerEmail}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.importerAddress) && (
                    <>
                      <dt className="col-sm-5">Importer Address</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.importerAddress}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.seasonCollection) && (
                    <>
                      <dt className="col-sm-5">Season / Collection</dt>
                      <dd className="col-sm-7">
                        {payload.metadata.seasonCollection}
                      </dd>
                    </>
                  )}
                  {hasText(targetAudience) && (
                    <>
                      <dt className="col-sm-5">Target Audience</dt>
                      <dd className="col-sm-7">{targetAudience}</dd>
                    </>
                  )}
                  {hasText(payload.metadata.machineClassification) && (
                    <>
                      <dt className="col-sm-5">Machine Classification</dt>
                      <dd className="col-sm-7">
                        {getMachineClassificationLabel(
                          payload.metadata.machineClassification,
                        )}
                      </dd>
                    </>
                  )}
                  {(hasText(ceResponsiblePersonSummary) ||
                    hasText(payload.metadata.ceResponsiblePerson)) && (
                    <>
                      <dt className="col-sm-5">CE Responsible Person</dt>
                      <dd className="col-sm-7">
                        {ceResponsiblePersonSummary ||
                          payload.metadata.ceResponsiblePerson}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.ceResponsiblePersonEntityType) && (
                    <>
                      <dt className="col-sm-5">CE Responsible Person Type</dt>
                      <dd className="col-sm-7">
                        {getCeResponsibleEntityTypeLabel(
                          payload.metadata.ceResponsiblePersonEntityType,
                        )}
                      </dd>
                    </>
                  )}
                  {hasText(payload.metadata.ceResponsiblePersonEuAddress) && (
                    <>
                      <dt className="col-sm-5">
                        CE Responsible Person Address
                      </dt>
                      <dd className="col-sm-7">
                        {payload.metadata.ceResponsiblePersonEuAddress}
                      </dd>
                    </>
                  )}
                  {hasText(
                    payload.metadata.ceResponsiblePersonContactEmail,
                  ) && (
                    <>
                      <dt className="col-sm-5">
                        CE Responsible Person Contact Email
                      </dt>
                      <dd className="col-sm-7">
                        {payload.metadata.ceResponsiblePersonContactEmail}
                      </dd>
                    </>
                  )}
                  {payload.machineryDeclaration && (
                    <>
                      <dt className="col-sm-5">
                        {isPartlyCompletedMachine
                          ? 'Declaration of Incorporation'
                          : 'Declaration of Conformity'}
                      </dt>
                      <dd className="col-sm-7">
                        <div className="d-flex flex-wrap align-items-center gap-2">
                          <Badge bg="success">
                            <i className="bi bi-check-lg me-1" />
                            Official Document
                          </Badge>
                          {hasDocumentAccess &&
                          payload.machineryDeclaration.url ? (
                            <a
                              href={payload.machineryDeclaration.url}
                              target="_blank"
                              rel="noreferrer"
                              className="btn btn-outline-dark btn-sm"
                            >
                              {getMachineryDeclarationTitle(
                                payload.machineryDeclaration,
                              )}
                            </a>
                          ) : (
                            <span className="small text-muted">
                              {getMachineryDeclarationTitle(
                                payload.machineryDeclaration,
                              )}
                            </span>
                          )}
                        </div>
                      </dd>
                    </>
                  )}
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col xs={12}>
            <Card className="public-dpp-card shadow-sm h-100">
              <Card.Body className="p-4">
                <div className="text-uppercase small fw-semibold text-muted mb-3">
                  Circularity
                </div>
                {technicalSpecsVisible ? (
                  <>
                    <dl className="row mb-0">
                      {hasNumber(payload.circularity.carbonFootprint) && (
                        <>
                          <dt className="col-sm-6">Carbon Footprint</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.carbonFootprint,
                              ' kg CO2e',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.expectedLifespan) && (
                        <>
                          <dt className="col-sm-6">Expected Lifespan</dt>
                          <dd className="col-sm-6">
                            {`${payload.circularity.expectedLifespan} ${payload.metadata.lifespanUnit}`}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.repairabilityScore) && (
                        <>
                          <dt className="col-sm-6">Repairability Score</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.repairabilityScore,
                              ' / 10',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.waterConsumption) && (
                        <>
                          <dt className="col-sm-6">Water Consumption</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.waterConsumption,
                              ' L',
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(payload.circularity.colorFastnessRating) && (
                        <>
                          <dt className="col-sm-6">Color Fastness</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.colorFastnessRating,
                            )}
                          </dd>
                        </>
                      )}
                      {hasNumber(
                        payload.circularity.pillingResistanceRating,
                      ) && (
                        <>
                          <dt className="col-sm-6">Pilling Resistance</dt>
                          <dd className="col-sm-6">
                            {formatNumber(
                              payload.circularity.pillingResistanceRating,
                            )}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.sparePartsAvailability) && (
                        <>
                          <dt className="col-sm-6">Spare Parts Availability</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.sparePartsAvailability}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.takeBackScheme) && (
                        <>
                          <dt className="col-sm-6">Take-Back Scheme</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.takeBackScheme}
                          </dd>
                        </>
                      )}
                      {hasText(payload.circularity.microplasticsStatus) && (
                        <>
                          <dt className="col-sm-6">Microplastics Status</dt>
                          <dd className="col-sm-6">
                            {titleCase(payload.circularity.microplasticsStatus)}
                          </dd>
                        </>
                      )}
                      {payload.circularity.microplasticsReport?.url && (
                        <>
                          <dt className="col-sm-6">
                            Microplastics Shedding Report
                          </dt>
                          <dd className="col-sm-6">
                            <a
                              href={payload.circularity.microplasticsReport.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {payload.circularity.microplasticsReport.title ||
                                'Open Microplastics Shedding Report'}
                            </a>
                          </dd>
                        </>
                      )}
                      {hasText(payload.metadata.softwareSupportExpiry) && (
                        <>
                          <dt className="col-sm-6">Software Support Expiry</dt>
                          <dd className="col-sm-6">
                            {formatDate(payload.metadata.softwareSupportExpiry)}
                          </dd>
                        </>
                      )}
                      {hasText(payload.metadata.energyEfficiencyRating) && (
                        <>
                          <dt className="col-sm-6">Energy Efficiency Rating</dt>
                          <dd className="col-sm-6">
                            {payload.metadata.energyEfficiencyRating}
                          </dd>
                        </>
                      )}
                    </dl>
                    <div className="mt-3">
                      <div className="small text-muted mb-1">
                        Recycling Instructions
                      </div>
                      <div>
                        {payload.circularity.recyclingInstructions ||
                          'No recycling instructions published.'}
                      </div>
                    </div>
                    {payload.circularity.careSymbols.length > 0 && (
                      <div className="mt-3">
                        <div className="small text-muted mb-2">
                          Care Symbols
                        </div>
                        <div className="d-flex flex-wrap gap-2">
                          {payload.circularity.careSymbols.map((symbol) => (
                            <Badge
                              key={symbol}
                              bg="light"
                              text="dark"
                              className="border"
                            >
                              {symbol}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {(payload.circularity.repairManual ||
                      payload.circularity.disassemblyGuide) && (
                      <div className="mt-3 d-flex flex-wrap gap-2">
                        {payload.circularity.repairManual && (
                          <a
                            href={payload.circularity.repairManual}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                          >
                            Repair Manual PDF
                          </a>
                        )}
                        {payload.circularity.disassemblyGuide && (
                          <a
                            href={payload.circularity.disassemblyGuide}
                            target="_blank"
                            rel="noreferrer"
                            className="btn btn-outline-secondary btn-sm"
                          >
                            Disassembly Instructions PDF
                          </a>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <Alert variant="secondary" className="mb-0">
                    Technical specifications for this product require restricted
                    access.
                  </Alert>
                )}
              </Card.Body>
            </Card>
          </Col>
          {technicalSpecsVisible ? (
            <>
              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      Materials
                    </div>
                    {payload.materials.length > 0 ? (
                      <Table responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Material</th>
                            <th>Weight %</th>
                            <th>Recycled Content</th>
                            <th>Recyclable</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.materials.map((material) => (
                            <tr
                              key={`${material.materialName}-${material.weightPercentage}`}
                            >
                              <td>{material.materialName}</td>
                              <td>
                                {formatPercent(material.weightPercentage)}
                              </td>
                              <td>
                                {formatPercent(
                                  material.recycledContentPercentage,
                                )}
                              </td>
                              <td>{material.isRecyclable ? 'Yes' : 'No'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-muted">
                        No material data available.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                      <div className="text-uppercase small fw-semibold text-muted">
                        Packaging
                      </div>
                      <Badge
                        bg={
                          payload.metadata.includeConsumerPackaging
                            ? 'success'
                            : 'secondary'
                        }
                      >
                        {payload.metadata.includeConsumerPackaging
                          ? 'Included'
                          : 'No packaging'}
                      </Badge>
                    </div>
                    {payload.packaging.length > 0 ? (
                      <Table responsive className="mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Category</th>
                            <th>Material</th>
                            <th>Weight (g)</th>
                            <th>PCR %</th>
                            <th>Recyclability</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payload.packaging.map((item, index) => (
                            <tr key={`${item.packagingCategory}-${index}`}>
                              <td>
                                {getPackagingCategoryLabel(
                                  item.packagingCategory,
                                )}
                              </td>
                              <td>{titleCase(item.materialType)}</td>
                              <td>{formatNumber(item.weightGrams)}</td>
                              <td>{formatPercent(item.pcrContentPercent)}</td>
                              <td>{titleCase(item.recyclabilityStatus)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    ) : (
                      <div className="text-muted">
                        {payload.metadata.includeConsumerPackaging
                          ? 'No packaging data available.'
                          : 'Item comes with no packaging.'}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              {isBatteryProduct && payload.battery && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Battery Technical & Safety Data
                      </div>
                      <dl className="row mb-0">
                        {hasNumber(payload.battery.ratedCapacityAh) && (
                          <>
                            <dt className="col-sm-4">Rated Capacity</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.ratedCapacityAh,
                                ' Ah',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.ratedEnergyKwh) && (
                          <>
                            <dt className="col-sm-4">Rated Energy</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.ratedEnergyKwh,
                                ' kWh',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.nominalVoltageV) && (
                          <>
                            <dt className="col-sm-4">Nominal Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.nominalVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.minimumVoltageV) && (
                          <>
                            <dt className="col-sm-4">Minimum Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.minimumVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(payload.battery.maximumVoltageV) && (
                          <>
                            <dt className="col-sm-4">Maximum Voltage</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.maximumVoltageV,
                                ' V',
                              )}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.powerCapabilityLimits) && (
                          <>
                            <dt className="col-sm-4">C-rate / Power Limits</dt>
                            <dd className="col-sm-8">
                              {payload.battery.powerCapabilityLimits}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.battery.roundTripEfficiencyPercent,
                        ) && (
                          <>
                            <dt className="col-sm-4">Round-trip Efficiency</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.battery.roundTripEfficiencyPercent,
                                ' %',
                              )}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.cycleLifetimeDescriptor) && (
                          <>
                            <dt className="col-sm-4">Cycle Lifetime</dt>
                            <dd className="col-sm-8">
                              {payload.battery.cycleLifetimeDescriptor}
                            </dd>
                          </>
                        )}
                        {hasText(
                          payload.battery.operatingTemperatureWindow,
                        ) && (
                          <>
                            <dt className="col-sm-4">Temperature Window</dt>
                            <dd className="col-sm-8">
                              {payload.battery.operatingTemperatureWindow}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.extinguishingAgents) && (
                          <>
                            <dt className="col-sm-4">Extinguishing Agents</dt>
                            <dd className="col-sm-8">
                              {payload.battery.extinguishingAgents}
                            </dd>
                          </>
                        )}
                        {hasText(payload.battery.safetyHandlingMeasures) && (
                          <>
                            <dt className="col-sm-4">Safety Handling</dt>
                            <dd className="col-sm-8">
                              {payload.battery.safetyHandlingMeasures}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isConstructionProduct &&
                payload.categorySpecific?.construction && (
                  <Col xs={12}>
                    <Card className="public-dpp-card shadow-sm">
                      <Card.Body className="p-4">
                        <div className="text-uppercase small fw-semibold text-muted mb-3">
                          Construction Product Data
                        </div>
                        <dl className="row mb-0">
                          {hasText(
                            payload.categorySpecific.construction
                              .vocEmissionClass,
                          ) && (
                            <>
                              <dt className="col-sm-4">VOC Emission Class</dt>
                              <dd className="col-sm-8">
                                {
                                  payload.categorySpecific.construction
                                    .vocEmissionClass
                                }
                              </dd>
                            </>
                          )}
                          {payload.categorySpecific.construction.dopDocument
                            ?.url && (
                            <>
                              <dt className="col-sm-4">
                                Declaration of Performance
                              </dt>
                              <dd className="col-sm-8">
                                <a
                                  href={
                                    payload.categorySpecific.construction
                                      .dopDocument.url
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {payload.categorySpecific.construction
                                    .dopDocument.title || 'Open DoP PDF'}
                                </a>
                              </dd>
                            </>
                          )}
                          {payload.categorySpecific.construction.demolitionGuide
                            ?.url && (
                            <>
                              <dt className="col-sm-4">Demolition Guide</dt>
                              <dd className="col-sm-8">
                                <a
                                  href={
                                    payload.categorySpecific.construction
                                      .demolitionGuide.url
                                  }
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {payload.categorySpecific.construction
                                    .demolitionGuide.title ||
                                    'Open Demolition Guide'}
                                </a>
                              </dd>
                            </>
                          )}
                        </dl>
                      </Card.Body>
                    </Card>
                  </Col>
                )}

              {isFurnitureProduct && payload.categorySpecific?.furniture && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      {(() => {
                        const flameRetardantChemicalNames =
                          payload.categorySpecific?.furniture
                            ?.flameRetardantChemicalNames ?? [];

                        return (
                          <>
                            <div className="text-uppercase small fw-semibold text-muted mb-3">
                              Furniture Circularity & Compliance
                            </div>
                            <dl className="row mb-0">
                              {hasText(
                                payload.categorySpecific.furniture
                                  .eudrDdsReference,
                              ) && (
                                <>
                                  <dt className="col-sm-4">
                                    EUDR DDS Reference
                                  </dt>
                                  <dd className="col-sm-8">
                                    {
                                      payload.categorySpecific.furniture
                                        .eudrDdsReference
                                    }
                                  </dd>
                                </>
                              )}
                              {payload.categorySpecific.furniture
                                .hasRemovableCovers !== null && (
                                <>
                                  <dt className="col-sm-4">Removable Covers</dt>
                                  <dd className="col-sm-8">
                                    {payload.categorySpecific.furniture
                                      .hasRemovableCovers
                                      ? 'Yes'
                                      : 'No'}
                                  </dd>
                                </>
                              )}
                              <dt className="col-sm-4">
                                Flame Retardants Present
                              </dt>
                              <dd className="col-sm-8">
                                {payload.categorySpecific.furniture
                                  .flameRetardantsPresent
                                  ? 'Yes'
                                  : 'No'}
                              </dd>
                              {flameRetardantChemicalNames.length > 0 && (
                                <>
                                  <dt className="col-sm-4">
                                    Flame Retardant Chemicals
                                  </dt>
                                  <dd className="col-sm-8">
                                    {flameRetardantChemicalNames.join(', ')}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isMetalsProduct && payload.categorySpecific?.metals && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Metals Production Data
                      </div>
                      <dl className="row mb-0">
                        {hasText(
                          payload.categorySpecific.metals.productionRoute,
                        ) && (
                          <>
                            <dt className="col-sm-4">Production Route</dt>
                            <dd className="col-sm-8">
                              {titleCase(
                                payload.categorySpecific.metals.productionRoute,
                              )}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.categorySpecific.metals.cbamEmbeddedEmissions,
                        ) && (
                          <>
                            <dt className="col-sm-4">
                              CBAM Embedded Emissions
                            </dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.categorySpecific.metals
                                  .cbamEmbeddedEmissions,
                                ' tCO2e/t',
                              )}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isTiresProduct && payload.categorySpecific?.tires && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      <div className="text-uppercase small fw-semibold text-muted mb-3">
                        Tire Performance Data
                      </div>
                      <dl className="row mb-0">
                        {hasText(
                          payload.categorySpecific.tires.rollingResistanceClass,
                        ) && (
                          <>
                            <dt className="col-sm-4">
                              Rolling Resistance Class
                            </dt>
                            <dd className="col-sm-8">
                              {
                                payload.categorySpecific.tires
                                  .rollingResistanceClass
                              }
                            </dd>
                          </>
                        )}
                        {hasText(
                          payload.categorySpecific.tires.wetGripClass,
                        ) && (
                          <>
                            <dt className="col-sm-4">Wet Grip Class</dt>
                            <dd className="col-sm-8">
                              {payload.categorySpecific.tires.wetGripClass}
                            </dd>
                          </>
                        )}
                        {hasNumber(
                          payload.categorySpecific.tires.abrasionRateMgKm,
                        ) && (
                          <>
                            <dt className="col-sm-4">Abrasion Rate</dt>
                            <dd className="col-sm-8">
                              {formatNumber(
                                payload.categorySpecific.tires.abrasionRateMgKm,
                                ' mg/km',
                              )}
                            </dd>
                          </>
                        )}
                        {payload.categorySpecific.tires.isRetreadable !==
                          null && (
                          <>
                            <dt className="col-sm-4">Retreadable</dt>
                            <dd className="col-sm-8">
                              {payload.categorySpecific.tires.isRetreadable
                                ? 'Yes'
                                : 'No'}
                            </dd>
                          </>
                        )}
                      </dl>
                    </Card.Body>
                  </Card>
                </Col>
              )}

              {isChemicalsProduct && payload.categorySpecific?.chemicals && (
                <Col xs={12}>
                  <Card className="public-dpp-card shadow-sm">
                    <Card.Body className="p-4">
                      {(() => {
                        const clpHazardPhrases =
                          payload.categorySpecific?.chemicals
                            ?.clpHazardPhrases ?? [];

                        return (
                          <>
                            <div className="text-uppercase small fw-semibold text-muted mb-3">
                              Chemical Compliance Data
                            </div>
                            <dl className="row mb-0">
                              {hasText(
                                payload.categorySpecific.chemicals.ufiCode,
                              ) && (
                                <>
                                  <dt className="col-sm-4">UFI Code</dt>
                                  <dd className="col-sm-8">
                                    {payload.categorySpecific.chemicals.ufiCode}
                                  </dd>
                                </>
                              )}
                              {clpHazardPhrases.length > 0 && (
                                <>
                                  <dt className="col-sm-4">
                                    CLP Hazard Phrases
                                  </dt>
                                  <dd className="col-sm-8">
                                    {clpHazardPhrases.join(', ')}
                                  </dd>
                                </>
                              )}
                              {hasNumber(
                                payload.categorySpecific.chemicals
                                  .surfactantBiodegradabilityPercentage,
                              ) && (
                                <>
                                  <dt className="col-sm-4">
                                    Surfactant Biodegradability
                                  </dt>
                                  <dd className="col-sm-8">
                                    {formatNumber(
                                      payload.categorySpecific.chemicals
                                        .surfactantBiodegradabilityPercentage,
                                      ' %',
                                    )}
                                  </dd>
                                </>
                              )}
                            </dl>
                          </>
                        );
                      })()}
                    </Card.Body>
                  </Card>
                </Col>
              )}

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      REACH
                    </div>
                    <div className="mb-3">
                      <div className="small text-muted">SVHC Status</div>
                      <div className="fw-semibold">
                        {titleCase(payload.reach.svhcStatus || 'not_declared')}
                      </div>
                    </div>
                    {payload.reach.svhcDeclarations.length > 0 ? (
                      <div className="d-flex flex-column gap-3">
                        {payload.reach.svhcDeclarations.map((item, index) => (
                          <div key={`${item.substanceName}-${index}`}>
                            <div className="fw-semibold">
                              {item.substanceName || '-'}
                            </div>
                            <div className="small text-muted">
                              {item.casEcNumber || '-'}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-muted">
                        No SVHC entries published.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      RoHS
                    </div>
                    <div className="fw-semibold mb-3">
                      {getRohsStatusLabel(payload.rohs.rohsStatus)}
                    </div>
                    {payload.rohs.rohsStatus === 'with_exemption' && (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-5">Exemption</dt>
                        <dd className="col-sm-7">
                          {payload.rohs.exemptionCode === 'OTHER'
                            ? payload.rohs.customExemptionDetails || 'Other'
                            : payload.rohs.exemptionCode || '-'}
                        </dd>
                        {payload.rohs.exemptionCode === 'OTHER' &&
                          payload.rohs.customExemptionDetails && (
                            <>
                              <dt className="col-sm-5">Details</dt>
                              <dd className="col-sm-7">
                                {payload.rohs.customExemptionDetails}
                              </dd>
                            </>
                          )}
                      </dl>
                    )}
                    {payload.rohs.rohsStatus === 'not_applicable' && (
                      <dl className="row mb-0 small">
                        <dt className="col-sm-5">Reason</dt>
                        <dd className="col-sm-7">
                          {payload.rohs.exclusionReason || '-'}
                        </dd>
                      </dl>
                    )}
                    {technicalSpecsVisible &&
                      payload.rohs.supportingDocument && (
                        <div className="mt-3">
                          <div className="small text-muted mb-1">
                            Supporting Document
                          </div>
                          {hasDocumentAccess &&
                          payload.rohs.supportingDocument.url ? (
                            <a
                              href={payload.rohs.supportingDocument.url}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {payload.rohs.supportingDocument.title ||
                                'Open RoHS Supporting Document'}
                            </a>
                          ) : (
                            <Badge bg="success">
                              <i className="bi bi-check-lg me-1" />
                              Document Verified
                            </Badge>
                          )}
                        </div>
                      )}
                  </Card.Body>
                </Card>
              </Col>

              <Col lg={4}>
                <Card className="public-dpp-card shadow-sm h-100">
                  <Card.Body className="p-4">
                    <div className="text-uppercase small fw-semibold text-muted mb-3">
                      POPs & PFAS
                    </div>
                    <div className="fw-semibold mb-3">
                      {isBatteryProduct
                        ? getBatteryPfasStatusLabel(payload.popsPfas)
                        : popsPfasAppliesToProduct || payload.popsPfas.status
                          ? getPopsPfasStatusLabel(payload.popsPfas.status)
                          : 'Not applicable'}
                    </div>
                    {isBatteryProduct ? (
                      <>
                        <div className="small text-muted mb-1">PFAS Status</div>
                        <div>{getBatteryPfasStatusLabel(payload.popsPfas)}</div>
                        {hasText(payload.popsPfas.assessmentNote) && (
                          <div className="text-muted mt-3">
                            {payload.popsPfas.assessmentNote}
                          </div>
                        )}
                        {payload.popsPfas.supportingDocument && (
                          <div className="mt-3">
                            <div className="small text-muted mb-1">
                              Supporting Document
                            </div>
                            {hasDocumentAccess &&
                            payload.popsPfas.supportingDocument.url ? (
                              <a
                                href={payload.popsPfas.supportingDocument.url}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {payload.popsPfas.supportingDocument.title ||
                                  'Open POPs/PFAS Supporting Document'}
                              </a>
                            ) : (
                              <Badge bg="success">
                                <i className="bi bi-check-lg me-1" />
                                Document Verified
                              </Badge>
                            )}
                          </div>
                        )}
                      </>
                    ) : popsPfasAppliesToProduct ? (
                      payload.popsPfas.status === 'contains_restricted' ? (
                        <>
                          <div className="small text-muted mb-1">
                            Declared substance / exemption details
                          </div>
                          <div>{payload.popsPfas.details || '-'}</div>
                        </>
                      ) : payload.popsPfas.status === 'verified_compliant' ? (
                        <>
                          <div className="text-muted">
                            No prohibited POPs or PFAS have been declared for
                            this product.
                          </div>
                          {payload.popsPfas.supportingDocument && (
                            <div className="mt-3">
                              <div className="small text-muted mb-1">
                                Supporting Document
                              </div>
                              {hasDocumentAccess &&
                              payload.popsPfas.supportingDocument.url ? (
                                <a
                                  href={payload.popsPfas.supportingDocument.url}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  {payload.popsPfas.supportingDocument.title ||
                                    'Open POPs/PFAS Supporting Document'}
                                </a>
                              ) : (
                                <Badge bg="success">
                                  <i className="bi bi-check-lg me-1" />
                                  Document Verified
                                </Badge>
                              )}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="text-muted">
                          No verified POPs / PFAS declaration has been published
                          yet.
                        </div>
                      )
                    ) : (
                      <div className="text-muted">
                        No POPs / PFAS declaration has been published for this
                        product category.
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </>
          ) : (
            <Col xs={12}>
              <Card className="public-dpp-card shadow-sm">
                <Card.Body className="p-4 p-lg-5">
                  <div className="text-uppercase small fw-semibold text-muted mb-3">
                    Technical Specifications
                  </div>
                  <h2 className="h5 mb-3">Restricted access required</h2>
                  <p className="text-muted mb-0">
                    This passport is configured to keep materials, compliance,
                    packaging, and circularity specifications behind restricted
                    access.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          )}

          {hasExtendedAccess && payload.extendedData ? (
            <>
              <Col xs={12}>
                <Card className="public-dpp-card shadow-sm">
                  <Card.Body className="p-4">
                    <div className="d-flex flex-wrap justify-content-between gap-3 mb-3">
                      <div className="text-uppercase small fw-semibold text-muted">
                        {isConfidentialTier
                          ? 'Confidential Access Data'
                          : 'Restricted Access Data'}
                      </div>
                      <Badge
                        bg={isConfidentialTier ? 'danger' : 'warning'}
                        text={isConfidentialTier ? undefined : 'dark'}
                      >
                        {grantedTier}
                      </Badge>
                    </div>
                    <div className="text-muted mb-4">
                      {isConfidentialTier
                        ? 'Level 3 access exposes complete unredacted traceability and full compliance identifiers for authorities and internal administrators.'
                        : 'Level 2 access adds circularity and compliance details while masking supplier identity and facility location.'}
                    </div>
                    <div className="d-flex flex-column gap-4">
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Supply Chain Traceability
                        </div>
                        {payload.extendedData.traceability.length > 0 ? (
                          <Table responsive className="mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>Tier</th>
                                <th>Company</th>
                                <th>Country</th>
                                <th>Facility ID / Address</th>
                                <th>Process / Stage</th>
                              </tr>
                            </thead>
                            <tbody>
                              {payload.extendedData.traceability.map(
                                (row, index) => (
                                  <tr
                                    key={`${row.tier}-${row.companyName}-${index}`}
                                  >
                                    <td>{titleCase(row.tier)}</td>
                                    <td>{row.companyName || '-'}</td>
                                    <td>{row.countryCode || '-'}</td>
                                    <td>{row.facilityIdOrAddress || '-'}</td>
                                    <td>{row.processComponent || '-'}</td>
                                  </tr>
                                ),
                              )}
                            </tbody>
                          </Table>
                        ) : (
                          <div className="text-muted">
                            No extended traceability rows are available for this
                            access tier.
                          </div>
                        )}
                      </div>
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Circularity Additions
                        </div>
                        <dl className="row mb-0">
                          <dt className="col-sm-6">Critical Raw Materials</dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .criticalRawMaterials
                              ? 'Yes'
                              : 'No'}
                          </dd>
                          <dt className="col-sm-6">
                            Critical Raw Materials List
                          </dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .criticalRawMaterialsList.length > 0
                              ? payload.extendedData.compliance.criticalRawMaterialsList.join(
                                  ', ',
                                )
                              : '-'}
                          </dd>
                          <dt className="col-sm-6">Repairability Score</dt>
                          <dd className="col-sm-6">
                            {hasNumber(payload.circularity.repairabilityScore)
                              ? formatNumber(
                                  payload.circularity.repairabilityScore,
                                  ' / 10',
                                )
                              : '-'}
                          </dd>
                          <dt className="col-sm-6">Spare Parts Availability</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.sparePartsAvailability || '-'}
                          </dd>
                          <dt className="col-sm-6">Repair Manual</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.repairManual
                              ? 'Available'
                              : 'Not published'}
                          </dd>
                          <dt className="col-sm-6">Disassembly Guide</dt>
                          <dd className="col-sm-6">
                            {payload.circularity.disassemblyGuide
                              ? 'Available'
                              : 'Not published'}
                          </dd>
                        </dl>
                      </div>
                      <div>
                        <div className="small text-muted text-uppercase fw-semibold mb-2">
                          Compliance Identifiers
                        </div>
                        <dl className="row mb-0">
                          <dt className="col-sm-6">WEEE Registration</dt>
                          <dd className="col-sm-6">
                            {payload.extendedData.compliance
                              .weeeRegistrationNumber || '-'}
                          </dd>
                          {isConfidentialTier && (
                            <>
                              <dt className="col-sm-6">Importer EORI</dt>
                              <dd className="col-sm-6">
                                {payload.extendedData.compliance.importerEori ||
                                  '-'}
                              </dd>
                              <dt className="col-sm-6">Hardware Revision</dt>
                              <dd className="col-sm-6">
                                {payload.extendedData.compliance
                                  .hardwareRevision || '-'}
                              </dd>
                              {hasText(
                                payload.extendedData.compliance
                                  .rohsExemptionCode,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Exemption Code
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .rohsExemptionCode
                                    }
                                  </dd>
                                </>
                              )}
                              {hasText(
                                payload.extendedData.compliance
                                  .customExemptionDetails,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Custom Exemption Details
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .customExemptionDetails
                                    }
                                  </dd>
                                </>
                              )}
                              {hasText(
                                payload.extendedData.compliance
                                  .rohsExclusionReason,
                              ) && (
                                <>
                                  <dt className="col-sm-6">
                                    RoHS Exclusion Reason
                                  </dt>
                                  <dd className="col-sm-6">
                                    {
                                      payload.extendedData.compliance
                                        .rohsExclusionReason
                                    }
                                  </dd>
                                </>
                              )}
                            </>
                          )}
                        </dl>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </>
          ) : null}
        </Row>
      )}

      {!isWithdrawn && (
        <footer className="mt-4 pt-2">
          <Card className="public-dpp-card shadow-sm">
            <Card.Body className="p-4 d-flex flex-wrap align-items-center justify-content-between gap-3">
              <div>
                {previewMode
                  ? 'This preview reflects the public Digital Product Passport view only.'
                  : hasExtendedAccess
                    ? `Extended ${grantedTier.toLowerCase()} access is active for this Digital Product Passport.`
                    : 'You are viewing public information about the product.'}
              </div>
              {!previewMode && !hasExtendedAccess && onRequestAccess && (
                <Button
                  variant="outline-dark"
                  size="sm"
                  onClick={onRequestAccess}
                >
                  Request Restricted / Auditor Access
                </Button>
              )}
            </Card.Body>
          </Card>
        </footer>
      )}
    </Container>
  );
}
